import { z } from "zod";

export const offerSchema = z.object({
  areaId: z.string().min(1, "Area is required"),
  categoryId: z
    .string()
    .optional()
    .transform((val) => (val && val.trim() !== "" ? val : undefined)),
  condition: z.enum(["new", "like-new", "good", "fair"]).optional(),
  description: z
    .string()
    .min(20, "Description must be at least 20 characters")
    .max(2000, "Description must be less than 2000 characters"),
  email: z.string().email("Valid email is required"),
  firstName: z.string().optional(),
  imageUrl: z.string().url().optional().or(z.literal("")),
  lastName: z.string().optional(),
  offerType: z.enum(["PHYSICAL_ITEM", "FINANCIAL_ASSISTANCE", "PEOPLE_NEEDED"]),
  phone: z.string().optional(),
  quantity: z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (typeof val === "string") {
        const parsed = parseInt(val, 10);

        return isNaN(parsed) ? undefined : parsed;
      }

      return val;
    })
    .refine(
      (val) => val === undefined || (typeof val === "number" && val > 0),
      {
        message: "Quantity must be a positive number",
      },
    )
    .optional(),
  title: z
    .string()
    .min(5, "Title must be at least 5 characters")
    .max(200, "Title must be less than 200 characters"),
});

export const matchOfferToNeedSchema = z.object({
  needId: z.string().min(1, "Need ID is required"),
  offerId: z.string().min(1, "Offer ID is required"),
  quantity: z
    .union([z.string(), z.number()])
    .transform((val) => {
      if (typeof val === "string") {
        const parsed = parseInt(val, 10);

        return isNaN(parsed) ? 0 : parsed;
      }

      return val;
    })
    .refine((val) => typeof val === "number" && val > 0, {
      message: "Quantity must be a positive number",
    }),
});

export const updateOfferStatusSchema = z.object({
  note: z.string().optional(),
  offerId: z.string().min(1, "Offer ID is required"),
  status: z.enum(["reviewing", "declined", "fulfilled"]),
});

export type OfferFormData = z.infer<typeof offerSchema>;
export type MatchOfferToNeedData = z.infer<typeof matchOfferToNeedSchema>;
export type UpdateOfferStatusData = z.infer<typeof updateOfferStatusSchema>;
